import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';

import { Select } from '../../../shared/components/forms/select';
import { FeedPage } from './feed-page';

/** Right-column Identity card shared by every feed page: Tenant (a picker
 *  only on create — fixed afterwards), Provider, Feed ID, Created, plus the
 *  immutability note. */
@Component({
  selector: 'app-feed-identity-card',
  standalone: true,
  imports: [FormsModule, TranslocoDirective, DatePipe, Select],
  template: `
    <ng-container *transloco="let t; read: 'providers.feedPage'">
      <div class="rounded-card bg-card p-6 shadow-hairline">
        <div class="mb-4 font-display text-[12px] font-semibold uppercase tracking-wide text-ink-2">{{ t('cards.identity') }}</div>
        <div class="grid grid-cols-[92px_1fr] items-start gap-x-3 gap-y-3.5 text-[13px]">
          <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('fields.tenant') }}</span>
          @if (page.isCreate) {
            <app-select
              [placeholder]="page.tenantsLoading() ? t('fields.tenantsLoading') : t('fields.tenantPlaceholder')"
              [options]="page.tenantOptions()"
              [disabled]="page.tenantsLoading()"
              [ngModel]="page.tenantId()"
              (ngModelChange)="page.tenantId.set($event)"
              [ngModelOptions]="{ standalone: true }"
            ></app-select>
          } @else {
            <span class="text-ink-1">{{ page.tenantName() }}</span>
          }

          <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('meta.provider') }}</span>
          <span class="text-ink-1">{{ page.provider()?.name }}</span>

          <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('fields.feedId') }}</span>
          <span class="break-all font-num text-[12px] text-ink-2">{{ page.isCreate ? t('fields.assignedOnCreate') : page.feed()?.id }}</span>

          <span class="text-[11px] uppercase tracking-wide text-ink-3">{{ t('meta.created') }}</span>
          <span class="text-ink-1">
            @if (page.isCreate) {
              {{ t('fields.assignedOnCreate') }}
            } @else if (page.feed(); as f) {
              {{ f.createdAt | date: 'mediumDate' }}
            }
          </span>
        </div>
        <p class="m-0 mt-4 text-[12px] leading-[18px] text-ink-3">{{ t('identityNote') }}</p>
      </div>
    </ng-container>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedIdentityCardComponent {
  protected readonly page = inject(FeedPage);
}
